import React, { useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { Button } from "components/buttons/Button"

import { AdminBonus2Service } from "services/bonus/AdminBonus2Service"

import { downloadPromoCodesCsv } from "../../helpers/promo-code.helpers"
import { inputClassName } from "../bonus-config/bonus-form-ui"

const MAX_GENERATE = 50
const COLLAPSE_ABOVE = 20

interface Props {
  codes: string[]
  onChange: (codes: string[]) => void
  tenant: string
  refused?: { conflicting: string[]; invalid: string[] }
  savedCodes?: string[]
  disabled?: boolean
  disabledReason?: string
  bonusName?: string
}

export const PromoCodeListEditor = ({
  codes,
  onChange,
  tenant,
  refused,
  savedCodes = [],
  disabled = false,
  disabledReason,
  bonusName
}: Props) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [count, setCount] = useState<number>(10)
  const [generating, setGenerating] = useState(false)
  const [expanded, setExpanded] = useState(false)

  const setAt = (idx: number, value: string) => onChange(codes.map((c, i) => (i === idx ? value.toUpperCase() : c)))
  const removeAt = (idx: number) => onChange(codes.filter((_, i) => i !== idx))

  const handleGenerate = async () => {
    setGenerating(true)
    const generated = await AdminBonus2Service.generatePromoCodes(tenant, Math.min(count, MAX_GENERATE))
    setGenerating(false)
    if (!generated?.length) {
      toast.error(t("bonus2.promo.toastGenerateFailed") as string)
      return
    }
    // Appended, never replacing: a code the operator typed by hand is not a draft to overwrite.
    onChange([...codes.filter(Boolean), ...generated])
    setExpanded(true)
  }

  const reasonFor = (code: string): string | null => {
    if (refused?.conflicting.includes(code)) return t("bonus2.promo.codeTaken") as string
    if (refused?.invalid.includes(code)) return t("bonus2.promo.codeInvalid") as string
    return null
  }

  const shown = expanded || codes.length <= COLLAPSE_ABOVE ? codes : codes.slice(0, COLLAPSE_ABOVE)

  return (
    <div className="space-y-2">
      {shown.map((code, idx) => {
        const reason = reasonFor(code)
        return (
          <div key={idx}>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={code}
                onChange={(e) => setAt(idx, e.target.value)}
                placeholder={t("bonus2.promo.codePlaceholder") as string}
                className={`${inputClassName} ${reason ? "border-rose-500" : ""}`}
              />
              <Button
                variant="ghost"
                onClick={() => removeAt(idx)}
                ariaLabel={t("bonus2.promo.removeCode") as string}
                className="btn-square text-gray-400 hover:text-rose-400"
              >
                ✕
              </Button>
            </div>
            {reason && <p className="mt-1 text-xs text-rose-400">{reason}</p>}
          </div>
        )
      })}

      {codes.length > shown.length && (
        <button type="button" onClick={() => setExpanded(true)} className="text-xs text-[rgba(169,78,206,1)] hover:underline">
          {t("bonus2.promo.showAllCodes", { count: codes.length })}
        </button>
      )}

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Button
          size="sm"
          onClick={() => onChange([...codes, ""])}
          disabled={disabled}
          className="bg-transparent text-[rgba(169,78,206,1)] border-[rgba(169,78,206,0.8)] hover:bg-[rgba(169,78,206,0.7)] hover:text-white disabled:opacity-50"
        >
          + {t("bonus2.promo.addCode")}
        </Button>

        <input
          type="number"
          min={1}
          max={MAX_GENERATE}
          value={count}
          onChange={(e) => setCount(Math.min(Math.max(parseInt(e.target.value) || 1, 1), MAX_GENERATE))}
          disabled={disabled}
          className={`${inputClassName} !w-16 input-sm`}
        />
        <Button
          variant="primary"
          size="sm"
          onClick={handleGenerate}
          disabled={disabled || generating}
          className="disabled:opacity-50"
        >
          {generating ? t("bonus2.promo.generating") : t("bonus2.promo.generateUnique", { count })}
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => downloadPromoCodesCsv(savedCodes, bonusName)}
          disabled={savedCodes.length === 0}
          // Generated codes are not reserved until they are saved, so exporting them first would
          // hand out a list that the save may still refuse.
          title={savedCodes.length === 0 ? (t("bonus2.promo.csvNeedsSave") as string) : undefined}
          className="border border-white/10 disabled:opacity-50"
        >
          {t("bonus2.promo.downloadCsv")}
        </Button>
      </div>

      {disabled && disabledReason && <p className="text-xs text-amber-300">{disabledReason}</p>}
      <p className="text-xs text-gray-400">{t("bonus2.promo.editorHint")}</p>
    </div>
  )
}
